// Copyright (c) 2020 WiseTime. All rights reserved.

import { Action, createAction } from "@reduxjs/toolkit"
import {
  AddToTimeGroupRequestSchema,
  AttachTagRequestSchema,
  CancelPostTimeRequestSchema,
  ClearRowTimesheetFieldRequestSchema,
  ClearTimeGroupErrorsRequestSchema,
  ClearTimesheetFieldRequestSchema,
  CreateEmptyTimeGroupRequestSchema,
  CreateTimeGroupRequestSchema,
  DeclineTagSuggestionRequestSchema,
  DeleteTimeGroupRequestSchema,
  DeleteTimelineMarkerRequestSchema,
  DetachTagCategoryRequestSchema,
  DetachTagRequestSchema,
  EmptyTrashRequestSchema,
  ImmediatelyDeleteTimeRowsRequestSchema,
  ListDaySummariesRequestSchema,
  ListTimeRowsByDateRequestSchema,
  MergeTimeGroupsRequestSchema,
  RemoveFromTimeGroupRequestSchema,
  SelectRowTimesheetFieldOptionRequestSchema,
  SelectTimesheetFieldOptionRequestSchema,
  TimeGroup,
  TimeRow,
  TrashTimeRowsRequestSchema,
  UnTrashTimeRowsRequestSchema,
  UpdateTimeGroupDurationRequestSchema,
  UpdateTimeGroupRequestSchema,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { Date } from "../../generated/google/type/date_pb"
import { TagRefSchema } from "../../generated/wisetime/user/type/tag_ref_pb"
import { SiteRef } from "../../generated/wisetime/type/site_ref_pb"
import {
  CreateLeaveRequestSchema,
  DeleteLeaveRequestSchema,
  ListLeavesRequestSchema,
  ListMemberExpectationsRequestSchema,
} from "../../generated/legebill/site/postexpectation/v4/post_expectation_pb"
import { TimeZone } from "../../app/timeZone"
import {
  BatchTranslateTextsRequestSchema,
  GenerateNarrativeRequestSchema,
} from "../../generated/wisetime/user/assistant/v4/assistant_pb"
import { TimelineDate } from "./dateTime"
import {
  AddFavouriteTimesheetFieldOptionRequestSchema,
  BatchGetTimesheetFieldConfigsRequestSchema,
  BatchGetTimesheetFieldOptionsRequestSchema,
  ListFavouriteTimesheetFieldOptionsRequestSchema,
  ListRecentTimesheetFieldOptionsRequestSchema,
  ListTimesheetFieldsRequestSchema,
  RemoveFavouriteTimesheetFieldOptionRequestSchema,
  ReorderFavouriteTimesheetFieldOptionRequestSchema,
} from "../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { Init } from "../../app/rpc"

type AttachTagPayload = {
  attachTagRequest: Init<typeof AttachTagRequestSchema>,
  tag: Init<typeof TagRefSchema>,
}

type ReplaceTagPayload = {
  attachTagRequest: Init<typeof AttachTagRequestSchema>,
  detachTagRequest: Init<typeof DetachTagRequestSchema>,
  tag: Init<typeof TagRefSchema>,
  onError: () => void,
}

export type PostTimePayload = {
  timeGroups: TimeGroup[],
  currentSiteRef: SiteRef,
  roundingPreferenceSec: number,
}

type EmptyTrashPayload = {
  request: Init<typeof EmptyTrashRequestSchema>,
  onComplete: () => void,
}

export type DeleteLeavePayload = {
  deleteLeaveRequest: Init<typeof DeleteLeaveRequestSchema>,
  membershipId: string,
  date: Date,
}

export const getTimelineData = createAction<TimelineDate | undefined>("timelines/get-timeline-data")
export const getCurrentTimelineData = (): Action => getTimelineData()

type ListTimesheetFieldsPayload = {
  request: Init<typeof ListTimesheetFieldsRequestSchema>,
  includeOptions?: boolean,
}
export const listTimesheetFields = createAction<ListTimesheetFieldsPayload>("timelines/list-timesheet-fields")
export const listFavouriteTimesheetFieldOptions = createAction<Init<typeof ListFavouriteTimesheetFieldOptionsRequestSchema>>("timelines/list-favourite-timesheet-field-options")
export const listRecentTimesheetFieldOptions = createAction<Init<typeof ListRecentTimesheetFieldOptionsRequestSchema>>("timelines/list-recent-timesheet-field-options")
export const generateTimesheetFieldConfigs = createAction<TimeGroup[]>("timelines/generate-timesheet-field-configs")
export const generateRowTimesheetFieldConfigs = createAction<TimeRow[]>("timelines/generate-row-timesheet-field-configs")
export const batchGetTimesheetFieldConfigs = createAction<Init<typeof BatchGetTimesheetFieldConfigsRequestSchema>>("timelines/batch-get-timesheet-field-configs")
export const batchGetTimesheetFieldConfigsFailed = createAction<Init<typeof BatchGetTimesheetFieldConfigsRequestSchema>>("timelines/batch-get-timesheet-field-configs-failed")
export const batchGetTimesheetFieldOptions = createAction<Init<typeof BatchGetTimesheetFieldOptionsRequestSchema>>("timelines/batch-get-timesheet-field-options")
export const selectTimesheetFieldOption = createAction<Init<typeof SelectTimesheetFieldOptionRequestSchema>>("timelines/select-timesheet-field-option")
export const clearTimesheetField = createAction<Init<typeof ClearTimesheetFieldRequestSchema>>("timelines/clear-timesheet-field")
export const selectRowTimesheetFieldOption = createAction<Init<typeof SelectRowTimesheetFieldOptionRequestSchema>>("timelines/select-row-timesheet-field-option")
export const clearRowTimesheetField = createAction<Init<typeof ClearRowTimesheetFieldRequestSchema>>("timelines/clear-row-timesheet-field")
export const addFavouriteTimesheetFieldOption = createAction<Init<typeof AddFavouriteTimesheetFieldOptionRequestSchema>>("timelines/add-favourite-timesheet-field-option")
export const removeFavouriteTimesheetFieldOption = createAction<Init<typeof RemoveFavouriteTimesheetFieldOptionRequestSchema>>("timelines/remove-favourite-timesheet-field-option")
export const reorderFavouriteTimesheetFieldOption = createAction<Init<typeof ReorderFavouriteTimesheetFieldOptionRequestSchema>>("timelines/reorder-favourite-timesheet-field-option")

export const watchTimelineUpdates = createAction("timelines/watch-timeline-updates")
export const listDaySummaries = createAction<Init<typeof ListDaySummariesRequestSchema>>("timelines/list-day-summaries")

export const setTimeZone = createAction<TimeZone>("timelines/set-time-zone")

// Dispatched whenever we faced that timeline looks to be out of sync with the server
export const onTimelineOutOfSync = createAction("timelines/out-of-sync")
export const syncCurrentDay = createAction("timelines/on-sync-current-day")

export const attachTag = createAction<AttachTagPayload>("timelines/attach-tag")
export const replaceTag = createAction<ReplaceTagPayload>("timelines/replace-tag")

export const declineTagSuggestion = createAction<Init<typeof DeclineTagSuggestionRequestSchema>>("timelines/decline-tag-suggestion")

export type CreateTimeGroupPayload = {
  request: Init<typeof CreateTimeGroupRequestSchema>,
  post?: boolean,
}
export const createTimeGroup = createAction<CreateTimeGroupPayload>("timelines/create-time-group")
export const postTimeGroup = createAction<TimeGroup>("timelines/post-time-group")

// Add time row ids to time group, or remove time row ids from time group
// using a FieldMask with the latest time row ids
export const updateTimeGroup = createAction<Init<typeof UpdateTimeGroupRequestSchema>>("timelines/update-time-group")

export const updateTimeGroupDuration = createAction<Init<typeof UpdateTimeGroupDurationRequestSchema>>("timelines/update-time-group-duration")

export type CreateEmptyTimeGroupPayload = {
  request: Init<typeof CreateEmptyTimeGroupRequestSchema>,
  skipSelection?: boolean,
  createTimeGroupRowIndex?: number,
  onSuccess?: () => void,
  onError?: () => void,
}
export const createEmptyTimeGroup = createAction<CreateEmptyTimeGroupPayload>("timelines/create-empty-time-group")

export const addToTimeGroup = createAction<Init<typeof AddToTimeGroupRequestSchema>>("timelines/add-to-time-group")
export const removeFromTimeGroup = createAction<Init<typeof RemoveFromTimeGroupRequestSchema>>("timelines/remove-from-time-group")

export type MergeTimeGroupsPayload = {
  request: Init<typeof MergeTimeGroupsRequestSchema>,
  timeRowIds: string[],
}
export const mergeTimeGroups = createAction<MergeTimeGroupsPayload>("timelines/merge-time-groups")
export const mergeTimeGroupsFulfilled = createAction("timelines/merge-time-groups-fulfilled")
export const mergeTimeGroupsErrored = createAction("timelines/merge-time-groups-errored")
type DeleteTimeGroupsPayload = {
  request: Init<typeof DeleteTimeGroupRequestSchema>,
  selectedTimeRowsIds: string[],
  resetProgress: () => void,
}
export const deleteTimeGroups = createAction<DeleteTimeGroupsPayload>("timelines/delete-time-groups")

export const trashTimeRows = createAction<Init<typeof TrashTimeRowsRequestSchema>>("timelines/trash-time-rows")
export const untrashTimeRows = createAction<Init<typeof UnTrashTimeRowsRequestSchema>>("timelines/untrash-time-rows")
export const immediatelyDeleteTimeRows = createAction<Init<typeof ImmediatelyDeleteTimeRowsRequestSchema>>("timelines/immediately-delete-time-rows")

export const postTime = createAction<PostTimePayload>("timelines/postTime")
export const cancelPostTime = createAction<Init<typeof CancelPostTimeRequestSchema>>("timelines/cancelPostTime")

export const emptyTrash = createAction<EmptyTrashPayload>("timelines/empty-trash")
export const detachTag = createAction<Init<typeof DetachTagRequestSchema>>("timelines/detach-tag")

export const listTimeRowsByDate = createAction<Init<typeof ListTimeRowsByDateRequestSchema>>("timelines/list-time-rows-by-date")

export const clearTimeGroupErrors = createAction<Init<typeof ClearTimeGroupErrorsRequestSchema>>("timelines/clear-time-group-errors")

export const detachTagCategory = createAction<Init<typeof DetachTagCategoryRequestSchema>>("timelines/detach-tag-category")

export const warnMultipleTagsPersisted = createAction("timelines/warn-multiple-tags-persisted")
export const warnMultipleTagsPersistFailed = createAction("timelines/warn-multiple-tags-persist-failed")
export const loadWarnMultipleTagsFromStorage = createAction("timelines/load-warn-multiple-tags-from-storage")
export const loadWarnMultipleTagsFromStorageFailed = createAction("timelines/load-warn-multiple-tags-from-storage-failed")

export const actionPaneNoteIsHiddenPersisted = createAction("timelines/action-pane-note-is-hidden-persisted")
export const actionPaneNoteIsHiddenPersistFailed = createAction("timelines/action-pane-note-is-hidden-persist-failed")
export const loadActionPaneNoteIsHiddenFromStorage = createAction("timelines/load-action-pane-note-is-hidden-from-storage")
export const loadActionPaneNoteIsHiddenFromStorageFailed = createAction("timelines/load-action-pane-note-is-hidden-from-storage-failed")

export const tilesViewTypePersisted = createAction("timelines/tiles-view-type-persisted")
export const tilesViewTypePersistFailed = createAction("timelines/tiles-view-type-persist-failed")
export const loadTilesViewTypeFromStorage = createAction("timelines/load-tiles-view-type-from-storage")
export const loadTilesViewTypeFromStorageFailed = createAction("timelines/load-tiles-view-type-from-storage-failed")

export const tagsViewTypePersisted = createAction("timelines/tags-view-type-persisted")
export const tagsViewTypePersistFailed = createAction("timelines/tags-view-type-persist-failed")
export const loadTagsViewTypeFromStorage = createAction("timelines/load-tags-view-type-from-storage")
export const loadTagsViewTypeFromStorageFailed = createAction("timelines/load-tags-view-type-from-storage-failed")

export const timesheetFieldsViewTypePersisted = createAction("timelines/timesheet-fields-view-type-persisted")
export const timesheetFieldsViewTypePersistFailed = createAction("timelines/timesheet-fields-view-type-persist-failed")
export const loadTimesheetFieldsViewTypeFromStorage = createAction("timelines/load-timesheet-fields-view-type-from-storage")
export const loadTimesheetFieldsViewTypeFromStorageFailed = createAction("timelines/load-timesheet-fields-view-type-from-storage-failed")

export const autoNarrativePersisted = createAction("timelines/auto-narrative-persisted")
export const autoNarrativePersistFailed = createAction("timelines/auto-narrative-persist-failed")
export const loadAutoNarrativeFromStorage = createAction("timelines/load-auto-narrative-from-storage")
export const loadAutoNarrativeFromStorageFailed = createAction("timelines/load-auto-narrative-from-storage-failed")

export const timeGroupsDisplayModePersisted = createAction("timelines/time-groups-display-mode-persisted")
export const timeGroupsDisplayModePersistFailed = createAction("timelines/time-groups-display-mode-persist-failed")
export const loadTimeGroupsDisplayModeFromStorage = createAction("timelines/load-time-groups-display-mode-from-storage")
export const loadTimeGroupsDisplayModeFromStorageFailed = createAction("timelines/load-time-groups-display-mode-from-storage-failed")

export const emptyTrashDoNotAskAgainPersisted = createAction("timelines/empty-trash-do-not-ask-again-persisted")
export const emptyTrashDoNotAskAgainPersistFailed = createAction("timelines/empty-trash-do-not-ask-again-persist-failed")
export const loadEmptyTrashDoNotAskAgainFromStorage = createAction("timelines/load-empty-trash-do-not-ask-again-from-storage")
export const loadEmptyTrashDoNotAskAgainFromStorageFailed = createAction("timelines/load-empty-trash-do-not-ask-again-from-storage-failed")

export const welcomeVideoStatePersisted = createAction("timelines/welcome-video-state-persisted")
export const welcomeVideoStatePersistFailed = createAction("timelines/welcome-video-state-persist-failed")
export const loadWelcomeVideoStateFromStorage = createAction("timelines/load-welcome-video-state-from-storage")
export const loadWelcomeVideoStateFromStorageFailed = createAction("timelines/load-welcome-video-state-from-storage-failed")

export const timesheetFieldViewModePersisted = createAction("timelines/timesheet-field-view-mode-persisted")
export const timesheetFieldViewModePersistFailed = createAction("timelines/timesheet-field-view-mode-persist-failed")
export const loadTimesheetFieldViewModeFromStorage = createAction("timelines/load-timesheet-field-view-mode-from-storage")
export const loadTimesheetFieldViewModeFromStorageFailed = createAction("timelines/load-timesheet-field-view-mode-from-storage-failed")

export const listLeaves = createAction<Init<typeof ListLeavesRequestSchema>>("timelines/list-leaves")
export const listLeavesErrored = createAction("timelines/list-leaves-errored")
export const createLeave = createAction<Init<typeof CreateLeaveRequestSchema>>("timelines/create-leave")
export const deleteLeave = createAction<DeleteLeavePayload>("timelines/delete-leave")
export const listMemberExpectations = createAction<Init<typeof ListMemberExpectationsRequestSchema>>("timelines/list-member-expectations")
export const refreshMemberExpectations = createAction<Init<typeof ListMemberExpectationsRequestSchema>>("timelines/refresh-member-expectations")

export const generateNarrative = createAction<Init<typeof GenerateNarrativeRequestSchema>>("timelines/generate-narrative")
export const translateNarratives = createAction<Init<typeof BatchTranslateTextsRequestSchema>>("timelines/translate-narratives")

export const deleteTimelineMarker = createAction<Init<typeof DeleteTimelineMarkerRequestSchema>>("timelines/delete-timeline-marker")

export const checkPendingPostStatus = createAction("timelines/check-pending-post-status")

export const refreshTimesheetFieldConfigs = createAction("timelines/refresh-timesheet-field-configs")

export const periodicRefreshTimesheetFieldConfigs = createAction("timelines/periodic-refresh-timesheet-field-configs")
